/**
 * projects.ts — проекты и раздел «Контент»: списки воронок, квизов и экранов
 * оплаты, мастер создания воронки, шаблоны, перенос/копирование, фильтры
 * (WEB-1874, этап 3, порция S1).
 *
 * ГРАНИЦА РАЗДЕЛА: здесь язык АВТОРА (кабинет). Содержимое самих воронок —
 * их названия, тексты экранов, языки перевода — это данные, они не
 * переводятся ни в какую сторону. Поэтому окно подтверждения удаления
 * сверяет введённое имя с НАСТОЯЩИМ именем воронки, а не с переводом слова.
 *
 * Числительные («2 воронки» / «5 воронок») считает `plural` внутри своей
 * половины словаря: локаль там известна по построению, поэтому записана
 * литералом.
 */
import { defineDictionary } from '../define';
import { plural } from '../plural';

export const projectsStrings = defineDictionary(
  {
    // ── Причины отказов на строках списков (гейты прав и тарифа) ────────────
    editDeniedHint: 'Your role is not allowed to edit funnels',
    manageDeniedHint:
      'Not enough rights: publishing and copies are available to the project owner and admins',
    ownTransferDeniedHint:
      'Not enough rights: duplicating, moving and copying funnels is available to those allowed to edit them',
    resourceReadonlyHint:
      'Read-only: this resource is over your current plan limit. You can open and view it, but not save or publish. Return to the previous plan to edit it again',

    // ── Колонки таблиц ──────────────────────────────────────────────────────
    colName: 'Name',
    colStatus: 'Status',
    colPublicUrl: 'Public link',
    colCreated: 'Created',
    colCreatedProject: 'Created',
    colOwner: 'Owner',
    colComposition: 'Contents',
    colPayments: 'Payments',
    colDomains: 'Domains',

    // ── Состояние воронки ───────────────────────────────────────────────────
    funnelStatusActive: 'Active',
    funnelStatusBroken: 'Broken',
    funnelStatusPausedShort: 'Paused',
    resourceStatusActive: 'Active',
    resourceStatusPaused: 'Paused',

    // ── WEB-1983: места под картинку, оставшиеся без картинки ───────────────
    funnelMissingImagesHint: (count: number) =>
      `${count} image ${plural('en', count, {
        one: 'slot',
        other: 'slots',
      })} in this funnel ${plural('en', count, {
        one: 'has',
        other: 'have',
      })} no image — open the editor`,

    // ── WEB-2130 п.3: боевая версия отстаёт от редактора ────────────────────
    funnelLiveBehindHintQuiz:
      'Visitors see an older version of the quiz: the latest editor changes are not published yet. Publish them from the editor.',
    funnelLiveBehindHintPaywall:
      'Visitors see an older version of the pricing screen: the latest editor changes are not published yet. Publish them from the editor.',
    funnelLiveBehindHintBoth:
      'Visitors see older versions of the quiz and the pricing screen: the latest editor changes are not published yet. Publish them from the editor.',

    // ── Фильтры ─────────────────────────────────────────────────────────────
    filterAllTime: 'All time',
    filterDays: (days: number) => `${days} days`,
    filterProviderOther: 'Other',
    filterOwnerLabel: 'Owner',
    filterStatusLabel: 'Status',
    filterCompositionLabel: 'Contents',
    filterPaymentsLabel: 'Payments',
    filterCreatedLabel: 'Created',
    filterCompositionFull: 'Quiz + paywall',
    filterCompositionQuizOnly: 'Quiz only',
    filterCompositionPaywallOnly: 'Paywall only',

    // ── Общее по разделу «Контент» ──────────────────────────────────────────
    // WEB-3749 юнит 1: заголовок раздела — по эталону рескина
    // (design/cabinet-rescin/sections/content.jsx:15, :49), подзаголовка нет.
    contentTitle: 'Funnels',
    // WEB-3749 юнит 1: подпись кнопки копирования в ячейке адреса.
    urlCopy: 'Copy address',
    // WEB-2126 (решение владельца `2122-К13`): у проекта, который берёт деньги
    // сам, в разделе одна вкладка — «Квизы», и заголовок обязан называть то,
    // что в разделе лежит. Эталон: content-tables-mockup/content-app.jsx:1070.
    contentTitleQuizOnly: 'Quizzes',
    tabFunnels: 'Funnels',
    tabQuizzes: 'Quizzes',
    tabPaywalls: 'Paywalls',
    nothingFound: 'Nothing found',
    domain: 'Domain',
    edit: 'Edit',
    duplicate: 'Duplicate',
    enable: 'Enable',
    disable: 'Disable',
    /**
     * WEB-1368 п.2.14: среди выделенного нет ни одного опубликованного —
     * кнопка «Отключить» гасится, а причина называется подсказкой.
     */
    bulkUnpublishNothingSelected: 'No published items among the selected ones',
    publish: 'Publish',
    publishAgain: 'Publish again',
    unpublish: 'Unpublish',
    preview: 'Preview',
    createWithAi: 'Create with AI',
    templates: 'Templates',
    rowActionsFor: (name: string) => `Actions: ${name}`,
    moveToProject: 'Move to project',
    copyToProject: 'Copy to project',
    idCopied: 'ID copied',
    // WEB-2429 (решение владельца 23.09, перенос на квизы 05.10): по ID
    // приложение откроет только экран с собственной привязкой к домену.
    copyIdNoOwnRoutePaywallHint:
      'The app opens a paywall by ID only if the paywall has its own address. This paywall has none (inside a funnel it opens at the funnel address) — connect a domain to the paywall itself',
    copyIdNoOwnRouteQuizHint:
      'The app opens a quiz by ID only if the quiz has its own address. This quiz has none (inside a funnel it opens at the funnel address) — connect a domain to the quiz itself',

    // ── WEB-2116: выдача ссылки предпросмотра из таблицы воронок (окно
    // открывает пункт «Предпросмотр», WEB-3749) ────────────────────────────
    shareTableNextStep: 'What to do',
    shareBlockNoRightsTitle: 'Editing rights are required',
    shareBlockNoRightsWhat:
      'A preview link is issued by someone who can edit the funnel. Your role does not have these rights.',
    shareBlockNoRightsNext:
      'Ask the project owner to grant you the rights or to send the link themselves.',
    shareBlockPlanTitle: 'The project is locked by a plan downgrade',
    shareBlockPlanWhat:
      'While the funnel is over the plan limit, preview links are not issued — otherwise the funnel could be shared around the plan.',
    shareBlockPlanNext:
      'Upgrade the plan in the “Billing” section, then come back and try again.',
    shareBlockBrokenTitle: 'The funnel is broken',
    shareBlockBrokenWhat:
      'The funnel refers to a deleted part — there is nothing to show.',
    shareBlockBrokenNext:
      'Attach another quiz or paywall with “Repair” in this row’s menu, then try again.',
    shareBlockEmptyTitle: 'The funnel has no screens',
    shareBlockEmptyWhat: 'There is nothing to capture and show.',
    shareBlockEmptyNext: 'Add at least one screen in the editor and try again.',

    // ── Воронки: список ─────────────────────────────────────────────────────

    // ── WEB-2141: цель возврата кнопкой «назад» с чужой страницы ────────────
    returnTargetMenu: 'Return from an external page',
    returnTargetTitle: (name: string) =>
      `Return from an external page — “${name}”`,
    returnTargetIntro:
      'A visitor who reached your offer, went to an external page and pressed Back lands back on the last quiz screen with nowhere to go. Choose where to send them instead.',
    returnTargetOptionStore: 'App or app store',
    returnTargetOptionStoreHint:
      'By the visitor operating system, using the store links of this project',
    returnTargetOptionUrl: 'Custom address',
    returnTargetOptionUrlHint: 'Your site, your payment page, a separate offer',
    returnTargetOptionStay: 'Stay on the last screen',
    returnTargetOptionStayHint:
      'No interception — the return is still counted in the reports',
    returnTargetUrlLabel: 'Address',
    returnTargetUrlPlaceholder: 'https://example.com/offer',
    returnTargetWarnUrlMissing:
      'A custom address is selected, but the address is empty — the return will keep the visitor on the last screen',
    returnTargetWarnStoreMissing:
      'The app store is selected, but this project has no store links — fill them in under Project settings, otherwise the return has nowhere to go',
    returnTargetSaved: 'The return target has been saved',
    returnTargetSaveFailed: 'Could not save the return target',
    returnTargetPublishWarning: (name: string) =>
      `The funnel “${name}” has been published, but its return target is unreachable — open “Return from an external page” in the row menu`,
    newFunnel: 'New funnel',
    createFunnel: 'Create funnel',
    funnelsLoadFailed: 'Could not load the funnels',
    funnelsEmptyTitle: 'No funnels yet',
    // WEB-2013 (строка 9): состав воронки назван прямо; текст — эталона
    // content.jsx (emptyFd), WEB-3749 юнит «таблица».
    funnelsEmptyDescription:
      'A funnel is a quiz and a paywall together.',
    funnelsEmptyOwnOnly:
      'Funnels you create will appear here. Other members’ funnels are not shown to you.',
    // WEB-3749 (таблица воронок на ките, эталон content.jsx:13-79): подписи,
    // которых нет у общих ключей квизов и пейволлов (те ключи — их вкладок).
    ftColName: 'Name',
    ftColUrl: 'Address',
    ftColCreated: 'Created',
    ftColActions: 'Actions',
    ftSelectAll: 'Select all',
    ftQuizPaywall: 'quiz + paywall',
    ftQuizOnly: 'quiz only',
    ftPaywallOnly: 'paywall only',
    ftSearch: 'Search by name…',
    ftAll: 'All',
    ftReset: 'Reset',
    ftSelected: (count: number) => `${count} selected`,
    ftPager: (from: number, to: number, total: number) =>
      `${from}–${to} of ${total}`,
    ftNothingHint: 'Try changing the filters.',
    ftOrgHint: 'Pick a project to see its funnels, quizzes and paywalls.',
    ftAnalytics: 'Analytics',
    ftRename: 'Rename',
    ftAddress: 'Funnel address…',
    ftOpenBuilder: 'Open in the builder',
    ftBrokenHint: 'The funnel is broken — fix it first',
    /**
     * WEB-2013 (строка 4): «All organization» mode — проекта нет, списки
     * квизов, пейволлов и воронок по построению пусты. Общая пара на все три
     * списка (WEB-1844: одно правило — одна копия текста). WEB-3749: подпись
     * под заголовком — ftOrgHint эталона, прежнее описание снято.
     */
    orgModeListTitle: 'No project selected',
    // WEB-1929: кусок мягко удалён — чип называет это словами и перестаёт
    // быть дверью в конструктор (иначе клик уводил на «Квиз не найден»).
    chipQuizDeleted: 'quiz deleted',
    chipPaywallDeleted: 'paywall deleted',
    chipQuizDeletedHint:
      'The quiz of this funnel has been deleted — the funnel is broken. Use "Repair" in the row menu to attach another quiz',
    chipPaywallDeletedHint:
      'The paywall of this funnel has been deleted — the funnel is broken. Use "Repair" in the row menu to attach another paywall',
    funnelHasNoQuiz: 'The funnel has no quiz',
    funnelHasNoPaywall: 'The funnel has no paywall',
    repair: 'Repair',
    // WEB-1929 (приёмка 12.09): «Repair» reconnects the MISSING piece to the
    // EXISTING funnel (PATCH funnel.id) — it must not be confused with the
    // blank creation wizard's `wizardTitle` above, which creates a new one.
    wizardRepairTitle: (name: string) => `Fix the funnel “${name}”`,
    wizardRepairFailed: 'Could not fix the funnel',
    // WEB-1929 (находка проверки) — a funnel record older than the piece-state
    // fields (quizState/paywallState both `undefined`, see funnelPieceState.ts)
    // leaves both needsQuizRepair/needsPaywallRepair false: "unknown" must not
    // be read as "the piece is fine". Without this text the modal showed no
    // form at all yet let "Fix" send an empty PATCH and claim success.
    wizardRepairUnknownState:
      'Could not determine what is missing — refresh the funnel list',
    // WEB-4114: имя — в заголовке вопросом, тело — без имени (эталон
    // content.jsx `offT` / `offD`; одно окно на воронки, квизы и пейволлы).
    unpublishTitle: (name: string) => `Unpublish “${name}”?`,
    unpublishBody:
      'Visitors will no longer see the funnel: if the project has app store links, the address will lead there.',
    funnelNameUpdated: 'The funnel name has been updated',
    funnelRenameFailed: 'Could not rename the funnel',
    funnelPublished: (name: string) =>
      `The funnel “${name}” has been published`,
    // WEB-1929: shown after «Repair» successfully reconnects the missing
    // piece — distinct from `funnelPublished` above (publishing, not repair).
    funnelRepaired: (name: string) => `The funnel “${name}” has been fixed`,
    funnelDeleted: (name: string) => `The funnel “${name}” has been deleted`,
    funnelDeleteFailed: 'Could not delete the funnel',
    funnelPublishFailed: 'Could not publish the funnel',
    // WEB-2426: блок отказа публикации над таблицей — причина целиком.
    funnelNotPublished: (name: string, reason: string) =>
      `The funnel “${name}” was not published: ${reason}`,
    publishRefusalOpenEditor: 'Open in the editor',
    funnelUnpublished: (name: string) =>
      `The funnel “${name}” has been unpublished`,
    funnelUnpublishFailed: 'Could not unpublish the funnel',
    // WEB-1920: пары для «Переместить в проект» / «Копировать в проект» у
    // воронки — зеркало quizMoved/quizCopied и paywallMoved/paywallCopied.
    funnelMoved: 'The funnel has been moved',
    funnelCopied: 'The funnel has been copied',
    funnelMoveCopyFailed: 'Could not move or copy the funnel',
    // WEB-3963: «Дублировать» воронку — успех (эталон content.jsx dupDone).
    funnelDuplicated: 'Copy created as a draft',
    previewPopupBlocked:
      'The preview opens in a new tab — allow pop-ups for this site',
    previewNothingToShow:
      'The funnel has neither a quiz nor a paywall to preview',
    previewFailed: 'Could not open the preview',
    deleteFunnelTitle: 'Delete the funnel',
    deleteFunnelConfirm: (name: string) =>
      `“${name}” will be deleted. This cannot be undone.`,
    deleteFunnelsTitle: (count: number) => `Delete ${count} funnels?`,
    deleteFunnelsBulkNote:
      'Quizzes and paywalls will stay in the library. This cannot be undone.',
    createFunnelInProject: 'Which project should the funnel be created in?',

    // ── Квизы: список и страница ────────────────────────────────────────────
    createQuiz: 'Create quiz',
    // WEB-3749 юнит «квизы»: пункт меню строки — окошко адреса квиза.
    quizAddress: 'Quiz address…',
    quizzesLoadFailed: 'Could not load the quizzes',
    quizzesEmptyTitle: 'No quizzes yet',
    quizzesEmptyDescription: 'Start from scratch or pick a ready template',
    createQuizInProject: 'Which project should the quiz be created in?',
    quizNameUpdated: 'The quiz name has been updated',
    quizMoved: 'The quiz has been moved',
    quizCopied: 'The quiz has been copied',
    quizFromTemplateFailed:
      'Could not create the quiz from the template. Try again.',
    // WEB-1955: запасная фраза для галереи шаблонов — раньше на экран шёл
    // английский текст из слайса, один на оба языка кабинета.
    templatesLoadFailed: 'Could not load the templates',
    quizDeleteFailed: 'Could not delete the quiz',
    quizUnpublishFailed: 'Could not unpublish the quiz',
    quizNotPublished: (name: string, reason: string) =>
      `The quiz “${name}” was not published: ${reason}`,
    quizUsedInFunnels: (count: number) =>
      ` It is used in ${count} active ${plural('en', count, {
        one: 'funnel',
        other: 'funnels',
      })} — they will become broken.`,
    deleteQuizTitle: 'Delete the quiz',
    deleteQuizConfirm: (name: string) =>
      `“${name}” will be deleted. This cannot be undone.`,
    deleteQuizzesTitle: (count: number) => `Delete ${count} quizzes?`,
    deleteBulkStatsKept: 'This cannot be undone. Statistics are kept.',
    disabledCount: (count: number) => `Disabled: ${count}`,
    disableFailedFor: (names: string) => `Could not disable: ${names}`,
    disabledPartially: (ok: number, total: number, names: string) =>
      `Disabled ${ok} of ${total}. Could not disable: ${names}`,

    // ── Экраны оплаты: список и страница ────────────────────────────────────
    createPaywall: 'Create paywall',
    paywallsLoadFailed: 'Could not load the paywalls',
    paywallsEmptyTitle: 'No paywalls yet',
    paywallsEmptyDescription: 'Start from scratch or pick a ready template',
    // WEB-3749 юнит 4: вкладка «Пейволлы» на ките (эталон content.jsx).
    pwFromTemplate: 'From template',
    pwCopyIdForApp: 'Copy ID for the app',
    pwAddress: 'Paywall address…',
    createPaywallInProject: 'Which project should the paywall be created in?',
    paywallNameUpdated: 'The paywall name has been updated',
    paywallMoved: 'The paywall has been moved',
    paywallCopied: 'The paywall has been copied',
    paywallFromTemplateFailed:
      'Could not create the paywall from the template. Try again.',
    paywallDeleteFailed: 'Could not delete the paywall',
    paywallUnpublishFailed: 'Could not unpublish the paywall',
    paywallNotPublished: (name: string, reason: string) =>
      `The paywall “${name}” was not published: ${reason}`,
    paywallUsedInFunnels: (count: number) =>
      ` It is used in ${count} active ${plural('en', count, {
        one: 'funnel',
        other: 'funnels',
      })}, which will become broken.`,
    deletePaywallTitle: 'Delete the paywall',
    deletePaywallConfirm: (name: string) =>
      `“${name}” will be deleted. This cannot be undone.`,
    deletePaywallsTitle: (count: number) => `Delete ${count} paywalls?`,

    // ── Модалки создания квиза и экрана оплаты ──────────────────────────────
    createQuizTitle: 'Create a quiz',
    create: 'Create',
    quizNameLabel: 'Quiz name',
    quizNameRequired: 'Enter the quiz name',
    quizLanguageLabel: 'Quiz language',
    quizLanguageHelp:
      'The language of the player’s own strings: “Next”, the plan period caption, the charge line, the cookie consent banner. You write the texts yourself, in any language.',
    createPaywallTitle: 'Create a paywall',
    paywallNameLabel: 'Paywall name',
    paywallNameRequired: 'Enter the paywall name',
    paywallLanguageLabel: 'Paywall language',
    paywallLanguageHelp:
      'The language of the player’s own strings: the plan period caption (“per month”), the charge line, payment failure messages. You write the texts yourself, in any language.',
    // WEB-3749: пустое значение списка языка в окнах создания — язык не
    // подставляется (WEB-1919), пока человек не выберет сам.
    createLanguageNotChosen: 'Not selected',

    // ── Проекты ─────────────────────────────────────────────────────────────
    projectsTitle: 'Projects',
    createProject: 'Create project',
    projectsLoadFailed: 'Could not load the projects',
    projectsEmpty: 'No projects yet',
    projectsEmptyBannerTitle: 'You have no projects yet',
    projectsEmptyBannerTextShort:
      'Create the first project to start building funnels and quizzes.',
    openProject: 'Open',
    projectSettings: 'Settings',
    openProjectFailed: 'Could not open the project',
    openProjectSettingsFailed: 'Could not open the project settings',
    deleteProjectTitle: (name: string) => `Delete the project “${name}”?`,
    deleteProjectOk: 'Delete project',
    deleteProjectFailed:
      'Could not delete the project. Check your password and try again.',
    // WEB-3760 юнит 2 (слово владельца 28.10): окно удаления по эталону
    // org.jsx:580–590 — TP.en delD, delDom, delName, delPass, deleted.
    deleteProjectText:
      'Every funnel, quiz and paywall goes too. This cannot be undone.',
    deleteProjectDomainsKept: 'Paid domains stay in the organization.',
    deleteProjectNameLabel: 'Type the project name to continue',
    deleteProjectPasswordLabel: 'Your password',
    projectDeleted: 'Project deleted',
    deleteProjectCascade: (
      quizzes: number,
      paywalls: number,
      invites: number,
    ) =>
      `The following will be deleted as well: ${quizzes} ${plural(
        'en',
        quizzes,
        {
          one: 'quiz',
          other: 'quizzes',
        },
      )}, ${paywalls} ${plural('en', paywalls, {
        one: 'paywall',
        other: 'paywalls',
      })}, ${invites} ${plural('en', invites, {
        one: 'invite',
        other: 'invites',
      })}. This cannot be undone.`,
    deleteProjectDomainsNote:
      'Paid domains are not deleted: they stay at the organization level (unassigned) and can be given to another project.',

    // ── Выбор проекта для создания ──────────────────────────────────────────
    selectCreationProjectTitle: 'Which project should it be created in?',
    selectCreationProjectContinue: 'Continue',
    selectCreationProjectText:
      'You are working in the “Entire organization” mode. Pick a project — we will create it there and open the editor.',

    // ── Перенос и копирование ───────────────────────────────────────────────
    moveTitle: 'Move to a project',
    copyTitle: 'Copy to a project',
    moveOk: 'Move',
    copyOk: 'Copy',
    moveNote:
      'The resource leaves this project; its history stays here marked (MOVED).',
    copyNote:
      'A clean copy is created in the chosen project (as a draft — publish it separately).',
    noOtherProjects: 'There are no other projects available.',
    // WEB-3749: подпись списка проектов и пометки строк «Затронутые ресурсы»
    // окна переноса и копии (эталон content-dlg.jsx, MoveCopy).
    moveCopyProject: 'Project',
    cascadePreviewLoading: 'Loading the preview of changes...',
    cascadeAffectedResources: 'Affected resources',
    cascadeSharedWith: (names: string) => `Used in: ${names}`,
    cascadeKindFunnel: 'funnel',
    cascadeKindQuiz: 'quiz',
    cascadeKindPaywall: 'paywall',
    cascadeBlocked:
      'The move is blocked: one or more items are used by other funnels of this project. Detach or move those funnels first.',
    // WEB-2123 (хвост WEB-1181): у копии воронки идентификаторы вопросов
    // выпускаются заново, и приложение клиента на копии молча перестаёт
    // узнавать ответы. Показывается, если к проекту назначения подключено
    // приложение (components/move-copy/copyIdsWarning.ts).
    copyNewIdsWarning:
      'The copy will get new question ids. The app connected to the selected project will not recognise answers in the copy until you update its mapping of question ids.',
    // WEB-2123: то же для «Дублировать» в ТОМ ЖЕ проекте (например, направление
    // A/B-теста) — «выбранного проекта» тут нет, поэтому отдельная пара.
    duplicateQuizTitle: 'Duplicate the quiz?',
    duplicateNewIdsWarning:
      'The duplicate will get new question ids. The app connected to this project will not recognise answers in the duplicate (for example, on an A/B test variant) until you update its mapping of question ids.',

    // ── Галерея шаблонов ────────────────────────────────────────────────────
    templateCategoryHealth: 'Health',
    templateCategoryFitness: 'Fitness',
    templateCategoryEducation: 'Education',
    templateCategoryLifestyle: 'Lifestyle',
    templateCategoryBusiness: 'Business',
    // WEB-4170: подписи категорий нишевых шаблонов и «Базовый» (дверь
    // components/templates/templateCategories.ts).
    templateCategoryBasic: 'Basic',
    templateCategoryAiCompanion: 'AI companion',
    templateCategoryIqTest: 'IQ test',
    templateCategoryAstrology: 'Astrology',
    templateCategoryIncome: 'Income',
    templateCategoryLanguages: 'Language learning',
    templateCategoryStories: 'Stories',
    templateCategoryBeauty: 'Beauty',
    templateCategoryMeditation: 'Meditation',
    templateCategoryMentalHealth: 'Mental health',
    templateCategoryRelationships: 'Relationships',
    templateCategoryNutrition: 'Nutrition',
    templateCategoryWeightLoss: 'Weight loss',
    templateCategoryProductivity: 'Productivity',
    templateCategoryReligion: 'Religion',
    templateCategoryParenting: 'Parenting',
    templateCategorySelfGrowth: 'Self-improvement',
    templateCategoryStyle: 'Style',
    templateCategoryUtilities: 'Utilities',
    templateCategoryFinance: 'Finance',
    templateCategoryHobbies: 'Hobbies',
    templateCategoryEcommerce: 'E-commerce',
    templateAllLanguages: 'All languages',
    templateAllCategories: 'All categories',
    templatePickQuiz: 'Pick a quiz template',
    templatePickPaywall: 'Pick a paywall template',
    templatesEmpty: 'No templates available',
    templateSearchPlaceholder: 'Search by name or description…',

    // ── Создание в проекте (хук useCreateInProject) ─────────────────────────
    noProjectToCreateIn:
      'There is no project you can create in. Ask for access to a project or create a new one.',
    switchProjectFailed: 'Could not switch the project',
    projectsStillLoading: 'Loading the project list — try again in a second.',
    projectNoLongerActive:
      'The project is no longer active — pick it again and repeat the creation.',
    creatingInProject: (projectName: string) =>
      `Creating in the project “${projectName}” — the cabinet has switched to it.`,

    // ── Окно «Новая воронка» и «Починить» (WEB-3749, эталон content-dlg.jsx) ─
    nfCreatedGo: 'Created — opening the builder',
    nfFixNotice:
      'Part of the funnel was deleted. Choose a replacement — the funnel comes back as a draft.',
    nfFix: 'Fix',
    nfDraftNote: 'A draft can’t go into a funnel — publish it first.',
    nfPartKept: 'Stays as it is',

    // ── Создание воронки (ключи окна «Новая воронка», имена — с мастера) ────
    wizardTitle: 'New funnel',
    wizardDuplicateName:
      'A funnel with this name already exists in the project. Change the name and try again.',
    wizardPaywallNoneWithQuiz: 'No paywall (quiz only)',
    wizardDraftSuffix: (name: string) => `${name} (draft)`,
    wizardNameRequired: 'The funnel name is required',
    wizardQuizSuffix: (name: string) => `${name} — quiz`,
    wizardPaywallSuffix: (name: string) => `${name} — paywall`,
    wizardCreateQuizFailed: 'Could not create the quiz',
    wizardCreateFunnelFailed: 'Could not create the funnel',
    wizardCreatePaywallFailed: 'Could not create the paywall',
    wizardCompositionErrorInline:
      'A funnel needs at least a quiz or a paywall.',
    wizardFunnelNameLabel: 'Funnel name',
    wizardQuizLabel: 'Quiz',
    wizardPaywallLabel: 'Paywall',
    wizardCreateNewQuiz: 'Create a new quiz',
    wizardPickFromLibrary: 'Pick from the library',
    wizardNoQuiz: 'No quiz (paywall only)',
    wizardSelectQuizPlaceholder: 'Pick a quiz from the list',
    wizardCreateNewPaywall: 'Create a new paywall',
    wizardSelectPaywallPlaceholder: 'Pick a paywall from the list',
    wizardEmptyPaywallWarning: 'The paywall will be created empty',
    // WEB-2443: колонка «Домены» в «Вся организация → Проекты» — сбой
    // загрузки списка доменов не выдаётся за «доменов нет».
    orgProjectDomainsNotLoaded: 'Not loaded',
    orgProjectDomainsNotLoadedHint:
      "Could not load the organization's domains. Refresh the page to try again.",
  },
  {
    editDeniedHint: 'У вашей роли нет права редактировать воронки',
    manageDeniedHint:
      'Недостаточно прав: управление публикацией и копиями доступно владельцу и администратору проекта',
    ownTransferDeniedHint:
      'Недостаточно прав: дублировать, переносить и копировать воронки может тот, кому разрешено их править',
    resourceReadonlyHint:
      'Только чтение: ресурс сверх лимита текущего тарифа. Открыть и посмотреть можно, сохранить и опубликовать — нет. Вернитесь на прежний тариф, чтобы снова редактировать',

    colName: 'Название',
    colStatus: 'Статус',
    colPublicUrl: 'Публичная ссылка',
    colCreated: 'Создано',
    colCreatedProject: 'Создан',
    colOwner: 'Владелец',
    colComposition: 'Состав',
    colPayments: 'Платежи',
    colDomains: 'Домены',

    funnelStatusActive: 'Активна',
    funnelStatusBroken: 'Сломана',
    funnelStatusPausedShort: 'Пауза',
    resourceStatusActive: 'Активен',
    resourceStatusPaused: 'Пауза',

    // WEB-1983: пометка на карточке воронки (места под картинку без картинки).
    funnelMissingImagesHint: (count: number) =>
      `В воронке ${count} ${plural('ru', count, {
        one: 'место',
        few: 'места',
        many: 'мест',
      })} под картинку без картинки — откройте редактор`,

    // WEB-2130 п.3: боевая версия отстаёт от редактора.
    funnelLiveBehindHintQuiz:
      'Посетители видят прежнюю версию квиза: последние правки из редактора ещё не опубликованы. Опубликуйте их в редакторе.',
    funnelLiveBehindHintPaywall:
      'Посетители видят прежнюю версию экрана с ценами: последние правки из редактора ещё не опубликованы. Опубликуйте их в редакторе.',
    funnelLiveBehindHintBoth:
      'Посетители видят прежние версии квиза и экрана с ценами: последние правки из редактора ещё не опубликованы. Опубликуйте их в редакторе.',

    filterAllTime: 'Всё время',
    filterDays: (days: number) =>
      `${days} ${plural('ru', days, { one: 'день', few: 'дня', many: 'дней' })}`,
    filterProviderOther: 'Другое',
    filterOwnerLabel: 'Владелец',
    filterStatusLabel: 'Статус',
    filterCompositionLabel: 'Состав',
    filterPaymentsLabel: 'Платежи',
    filterCreatedLabel: 'Дата создания',
    filterCompositionFull: 'Квиз + пейволл',
    filterCompositionQuizOnly: 'Только квиз',
    filterCompositionPaywallOnly: 'Только пейволл',

    contentTitle: 'Воронки',
    urlCopy: 'Скопировать адрес',
    contentTitleQuizOnly: 'Квизы',
    tabFunnels: 'Воронки',
    tabQuizzes: 'Квизы',
    tabPaywalls: 'Пейволлы',
    nothingFound: 'Ничего не найдено',
    domain: 'Домен',
    edit: 'Редактировать',
    duplicate: 'Дублировать',
    enable: 'Включить',
    disable: 'Отключить',
    bulkUnpublishNothingSelected: 'Среди выделенного нет опубликованных',
    publish: 'Опубликовать',
    publishAgain: 'Опубликовать снова',
    unpublish: 'Снять с публикации',
    // WEB-3749: пункт меню строки воронки — окно «Предпросмотр» (content.jsx:239).
    preview: 'Предпросмотр',
    createWithAi: 'Создать с ИИ',
    templates: 'Шаблоны',
    rowActionsFor: (name: string) => `Действия: ${name}`,
    moveToProject: 'Переместить в проект',
    copyToProject: 'Копировать в проект',
    idCopied: 'ID скопирован',
    copyIdNoOwnRoutePaywallHint:
      'По ID приложение откроет только экран с ценами с собственным адресом. У этого экрана своего адреса нет (в составе воронки он открывается по адресу воронки) — привяжите домен к самому экрану с ценами',
    copyIdNoOwnRouteQuizHint:
      'По ID приложение откроет только квиз с собственным адресом. У этого квиза своего адреса нет (в составе воронки он открывается по адресу воронки) — привяжите домен к самому квизу',

    // ── WEB-2116: выдача ссылки предпросмотра из таблицы воронок (окно
    // открывает пункт «Предпросмотр», WEB-3749) ────────────────────────────
    shareTableNextStep: 'Что сделать',
    shareBlockNoRightsTitle: 'Нужны права на редактирование',
    shareBlockNoRightsWhat:
      'Ссылку предпросмотра выдаёт тот, кто может править воронку. У вашей роли таких прав нет.',
    shareBlockNoRightsNext:
      'Попросите владельца проекта выдать права либо прислать ссылку самому.',
    shareBlockPlanTitle: 'Проект заперт понижением тарифа',
    shareBlockPlanWhat:
      'Пока воронка сверх лимита тарифа, ссылки предпросмотра не выдаются — иначе воронку можно было бы раздавать в обход тарифа.',
    shareBlockPlanNext:
      'Повысьте тариф в разделе «Биллинг», затем вернитесь и повторите.',
    shareBlockBrokenTitle: 'Воронка сломана',
    shareBlockBrokenWhat:
      'Воронка ссылается на удалённую часть — показывать нечего.',
    shareBlockBrokenNext:
      'Привяжите другой квиз или пейволл пунктом «Починить» в меню этой строки, затем повторите.',
    shareBlockEmptyTitle: 'В воронке нет ни одного экрана',
    shareBlockEmptyWhat: 'Фиксировать и показывать нечего.',
    shareBlockEmptyNext:
      'Добавьте хотя бы один экран в конструкторе и повторите.',

    // ── WEB-2141: цель возврата кнопкой «назад» с чужой страницы ────────────
    returnTargetMenu: 'Возврат с внешней страницы',
    returnTargetTitle: (name: string) =>
      `Возврат с внешней страницы — «${name}»`,
    returnTargetIntro:
      'Человек дошёл до предложения, ушёл на внешнюю страницу и нажал «назад» — и снова оказался на последнем экране квиза, откуда идти некуда. Выберите, куда его вести вместо этого.',
    returnTargetOptionStore: 'Приложение или магазин приложений',
    returnTargetOptionStoreHint:
      'По операционной системе посетителя, адресами магазинов этого проекта',
    returnTargetOptionUrl: 'Произвольный адрес',
    returnTargetOptionUrlHint:
      'Ваш сайт, ваша страница оплаты, отдельное предложение',
    returnTargetOptionStay: 'Оставить на последнем экране',
    returnTargetOptionStayHint:
      'Без перехвата — возврат всё равно попадает в отчёты',
    returnTargetUrlLabel: 'Адрес',
    returnTargetUrlPlaceholder: 'https://example.com/offer',
    returnTargetWarnUrlMissing:
      'Выбран произвольный адрес, но сам адрес не заполнен — возврат оставит человека на последнем экране',
    returnTargetWarnStoreMissing:
      'Выбран магазин приложений, но у проекта нет адресов магазина — заполните их в настройках проекта, иначе возврату некуда вести',
    returnTargetSaved: 'Цель возврата сохранена',
    returnTargetSaveFailed: 'Не удалось сохранить цель возврата',
    returnTargetPublishWarning: (name: string) =>
      `Воронка «${name}» опубликована, но её цель возврата недостижима — откройте «Возврат с внешней страницы» в меню строки`,
    newFunnel: 'Новая воронка',
    createFunnel: 'Создать воронку',
    funnelsLoadFailed: 'Не удалось загрузить воронки',
    funnelsEmptyTitle: 'Воронок пока нет',
    // WEB-2013 (строка 9): состав воронки назван прямо; текст — эталона
    // content.jsx (emptyFd), WEB-3749 юнит «таблица».
    funnelsEmptyDescription:
      'Воронка — это квиз и пейволл вместе.',
    funnelsEmptyOwnOnly:
      'Здесь появятся созданные вами воронки. Воронки других участников вам не показываются.',
    ftColName: 'Название',
    ftColUrl: 'Адрес',
    ftColCreated: 'Создана',
    ftColActions: 'Действия',
    ftSelectAll: 'Выбрать все',
    ftQuizPaywall: 'квиз + пейволл',
    ftQuizOnly: 'только квиз',
    ftPaywallOnly: 'только пейволл',
    ftSearch: 'Найти по названию…',
    ftAll: 'Все',
    ftReset: 'Сбросить',
    ftSelected: (count: number) => `Выбрано ${count}`,
    ftPager: (from: number, to: number, total: number) =>
      `${from}–${to} из ${total}`,
    ftNothingHint: 'Попробуйте изменить фильтры.',
    ftOrgHint: 'Выберите проект, чтобы увидеть его воронки, квизы и пейволлы.',
    ftAnalytics: 'Аналитика',
    ftRename: 'Переименовать',
    ftAddress: 'Адрес воронки…',
    ftOpenBuilder: 'Открыть в конструкторе',
    ftBrokenHint: 'Воронка сломана — сначала почините',
    orgModeListTitle: 'Проект не выбран',
    chipQuizDeleted: 'квиз удалён',
    chipPaywallDeleted: 'пейволл удалён',
    chipQuizDeletedHint:
      'Квиз этой воронки удалён — воронка сломана. Подключите другой квиз через «Починить» в меню строки',
    chipPaywallDeletedHint:
      'Пейволл этой воронки удалён — воронка сломана. Подключите другой пейволл через «Починить» в меню строки',
    funnelHasNoQuiz: 'В воронке нет квиза',
    funnelHasNoPaywall: 'В воронке нет пейволла',
    repair: 'Починить',
    wizardRepairTitle: (name: string) => `Починить воронку «${name}»`,
    wizardRepairFailed: 'Не удалось починить воронку',
    // WEB-1929 (находка проверки) — запись старше полей состояния кусков
    // (quizState/paywallState оба `undefined`, см. funnelPieceState.ts):
    // needsQuizRepair/needsPaywallRepair оба false — «неизвестно» нельзя
    // читать как «часть жива». Без этого текста окно не рисовало ни одной
    // формы, а «Починить» уходило пустым PATCH и объявляло успех.
    wizardRepairUnknownState:
      'Не удалось определить, чего не хватает — обновите список воронок',
    // WEB-4114: имя — в заголовке вопросом, тело — без имени (эталон
    // content.jsx `offT` / `offD`; одно окно на воронки, квизы и пейволлы).
    unpublishTitle: (name: string) => `Снять «${name}» с публикации?`,
    unpublishBody:
      'Посетители больше не увидят воронку: если в проекте заданы ссылки на магазины приложений, адрес будет вести туда.',
    funnelNameUpdated: 'Название воронки обновлено',
    funnelRenameFailed: 'Не удалось переименовать воронку',
    funnelPublished: (name: string) => `Воронка «${name}» опубликована`,
    funnelRepaired: (name: string) => `Воронка «${name}» починена`,
    funnelDeleted: (name: string) => `Воронка «${name}» удалена`,
    funnelDeleteFailed: 'Не удалось удалить воронку',
    funnelPublishFailed: 'Не удалось опубликовать воронку',
    // WEB-2426: блок отказа публикации над таблицей — причина целиком.
    funnelNotPublished: (name: string, reason: string) =>
      `Воронка «${name}» не опубликована: ${reason}`,
    publishRefusalOpenEditor: 'Открыть в конструкторе',
    funnelUnpublished: (name: string) => `Воронка «${name}» снята с публикации`,
    funnelUnpublishFailed: 'Не удалось снять воронку с публикации',
    // WEB-1920: пары для «Переместить в проект» / «Копировать в проект».
    funnelMoved: 'Воронка перемещена',
    funnelCopied: 'Воронка скопирована',
    funnelMoveCopyFailed: 'Не удалось переместить или скопировать воронку',
    // WEB-3963: «Дублировать» воронку — успех (эталон content.jsx dupDone).
    funnelDuplicated: 'Копия создана — черновиком',
    previewPopupBlocked:
      'Превью открывается в новой вкладке — разрешите всплывающие окна для сайта',
    previewNothingToShow: 'В воронке нет ни квиза, ни пейволла для превью',
    previewFailed: 'Не удалось открыть превью',
    deleteFunnelTitle: 'Удалить воронку',
    deleteFunnelConfirm: (name: string) =>
      `«${name}» удалится. Отменить нельзя.`,
    deleteFunnelsTitle: (count: number) =>
      `Удалить ${count} ${plural('ru', count, {
        one: 'воронку',
        few: 'воронки',
        many: 'воронок',
      })}?`,
    deleteFunnelsBulkNote:
      'Квизы и пейволлы останутся в библиотеке. Действие необратимо.',
    createFunnelInProject: 'В каком проекте создать воронку?',

    createQuiz: 'Создать квиз',
    quizAddress: 'Адрес квиза…',
    quizzesLoadFailed: 'Не удалось загрузить квизы',
    quizzesEmptyTitle: 'Квизов пока нет',
    quizzesEmptyDescription: 'Создайте с нуля или выберите готовый шаблон',
    createQuizInProject: 'В каком проекте создать квиз?',
    quizNameUpdated: 'Название квиза обновлено',
    quizMoved: 'Квиз перемещён',
    quizCopied: 'Квиз скопирован',
    quizFromTemplateFailed:
      'Не удалось создать квиз из шаблона. Попробуйте ещё раз.',
    templatesLoadFailed: 'Не удалось загрузить шаблоны',
    quizDeleteFailed: 'Не удалось удалить квиз',
    quizUnpublishFailed: 'Не удалось снять квиз с публикации',
    quizNotPublished: (name: string, reason: string) =>
      `Квиз «${name}» не опубликован: ${reason}`,
    quizUsedInFunnels: (count: number) =>
      ` Используется в активных воронках (${count}) — они станут «битыми».`,
    deleteQuizTitle: 'Удалить квиз',
    deleteQuizConfirm: (name: string) =>
      `«${name}» удалится. Отменить нельзя.`,
    deleteQuizzesTitle: (count: number) =>
      `Удалить ${count} ${plural('ru', count, {
        one: 'квиз',
        few: 'квиза',
        many: 'квизов',
      })}?`,
    deleteBulkStatsKept: 'Действие необратимо. Статистика сохранится.',
    disabledCount: (count: number) => `Отключено: ${count}`,
    disableFailedFor: (names: string) => `Не удалось отключить: ${names}`,
    disabledPartially: (ok: number, total: number, names: string) =>
      `Отключено ${ok} из ${total}. Не удалось: ${names}`,

    createPaywall: 'Создать пейволл',
    paywallsLoadFailed: 'Не удалось загрузить пейволлы',
    paywallsEmptyTitle: 'Пейволлов пока нет',
    paywallsEmptyDescription: 'Создайте с нуля или выберите готовый шаблон',
    // WEB-3749 юнит 4: вкладка «Пейволлы» на ките (эталон content.jsx).
    pwFromTemplate: 'Из шаблона',
    pwCopyIdForApp: 'Скопировать ID для приложения',
    pwAddress: 'Адрес пейволла…',
    createPaywallInProject: 'В каком проекте создать экран оплаты?',
    paywallNameUpdated: 'Название пейволла обновлено',
    paywallMoved: 'Пейволл перемещён',
    paywallCopied: 'Пейволл скопирован',
    paywallFromTemplateFailed:
      'Не удалось создать пейволл из шаблона. Попробуйте ещё раз.',
    paywallDeleteFailed: 'Не удалось удалить пейволл',
    paywallUnpublishFailed: 'Не удалось снять пейволл с публикации',
    paywallNotPublished: (name: string, reason: string) =>
      `Пейволл «${name}» не опубликован: ${reason}`,
    paywallUsedInFunnels: (count: number) =>
      ` Он используется в ${count} активных ${plural('ru', count, {
        one: 'воронке',
        few: 'воронках',
        many: 'воронках',
      })}, которые станут «Сломаны».`,
    deletePaywallTitle: 'Удалить пейволл',
    deletePaywallConfirm: (name: string) =>
      `«${name}» удалится. Отменить нельзя.`,
    deletePaywallsTitle: (count: number) =>
      `Удалить ${count} ${plural('ru', count, {
        one: 'пейволл',
        few: 'пейволла',
        many: 'пейволлов',
      })}?`,

    createQuizTitle: 'Создать квиз',
    create: 'Создать',
    quizNameLabel: 'Название квиза',
    quizNameRequired: 'Укажите название квиза',
    quizLanguageLabel: 'Язык квиза',
    quizLanguageHelp:
      'Язык служебных строк плеера: «Далее», подпись периода тарифа, строка списания, баннер согласия на куки. Тексты вы пишете сами, на любом языке.',
    createPaywallTitle: 'Создать пейволл',
    paywallNameLabel: 'Название пейволла',
    paywallNameRequired: 'Введите название пейволла',
    paywallLanguageLabel: 'Язык пейволла',
    paywallLanguageHelp:
      'Язык служебных строк плеера: подпись периода тарифа («в месяц»), строка списания, сообщения об отказе оплаты. Тексты вы пишете сами, на любом языке.',
    createLanguageNotChosen: 'Не выбран',

    projectsTitle: 'Проекты',
    createProject: 'Создать проект',
    projectsLoadFailed: 'Не удалось загрузить проекты',
    projectsEmpty: 'Проектов пока нет',
    projectsEmptyBannerTitle: 'У вас пока нет проектов',
    projectsEmptyBannerTextShort:
      'Создайте первый проект, чтобы начать собирать воронки и квизы.',
    openProject: 'Открыть',
    projectSettings: 'Настройки',
    openProjectFailed: 'Не удалось открыть проект',
    openProjectSettingsFailed: 'Не удалось открыть настройки проекта',
    deleteProjectTitle: (name: string) => `Удалить проект «${name}»?`,
    deleteProjectOk: 'Удалить проект',
    deleteProjectFailed:
      'Не удалось удалить проект. Проверьте пароль и попробуйте снова.',
    deleteProjectText:
      'Удалятся все воронки, квизы и пейволлы. Отменить нельзя.',
    deleteProjectDomainsKept: 'Оплаченные домены останутся в организации.',
    deleteProjectNameLabel: 'Введите название проекта, чтобы продолжить',
    deleteProjectPasswordLabel: 'Ваш пароль',
    projectDeleted: 'Проект удалён',
    deleteProjectCascade: (
      quizzes: number,
      paywalls: number,
      invites: number,
    ) =>
      `Каскадом удалятся: ${quizzes} ${plural('ru', quizzes, {
        one: 'квиз',
        few: 'квиза',
        many: 'квизов',
      })}, ${paywalls} ${plural('ru', paywalls, {
        one: 'пейволл',
        few: 'пейволла',
        many: 'пейволлов',
      })}, ${invites} ${plural('ru', invites, {
        one: 'инвайт',
        few: 'инвайта',
        many: 'инвайтов',
      })}. Действие необратимо.`,
    deleteProjectDomainsNote:
      'Оплаченные домены не удаляются: они сохранятся на уровне организации (без привязки к проекту) и их можно будет назначить другому проекту.',

    selectCreationProjectTitle: 'В каком проекте создать?',
    selectCreationProjectContinue: 'Продолжить',
    selectCreationProjectText:
      'Вы работаете в режиме «Вся организация». Выберите проект — создадим в нём и откроем конструктор.',

    moveTitle: 'Переместить в проект',
    copyTitle: 'Скопировать в проект',
    moveOk: 'Переместить',
    copyOk: 'Копировать',
    moveNote:
      'Ресурс покинет этот проект; история останется здесь с пометкой (ПЕРЕМЕЩЕНО).',
    copyNote:
      'В выбранном проекте будет создана чистая копия (как черновик — опубликуете отдельно).',
    noOtherProjects: 'Нет других доступных проектов.',
    moveCopyProject: 'Проект',
    cascadePreviewLoading: 'Загрузка предпросмотра изменений...',
    cascadeAffectedResources: 'Затронутые ресурсы',
    cascadeSharedWith: (names: string) => `Используется в: ${names}`,
    cascadeKindFunnel: 'воронка',
    cascadeKindQuiz: 'квиз',
    cascadeKindPaywall: 'пейволл',
    cascadeBlocked:
      'Перемещение заблокировано: один или несколько элементов используются в других воронках этого проекта. Сначала отвяжите или переместите другие воронки.',
    copyNewIdsWarning:
      'У копии идентификаторы вопросов будут новыми. Приложение, подключённое к выбранному проекту, не узнает ответы копии, пока вы не обновите в нём соответствие идентификаторов вопросов.',
    duplicateQuizTitle: 'Дублировать квиз?',
    duplicateNewIdsWarning:
      'У дубликата идентификаторы вопросов будут новыми. Приложение, подключённое к этому проекту, не узнает ответы дубликата (например, на направлении A/B-теста), пока вы не обновите в нём соответствие идентификаторов вопросов.',

    templateCategoryHealth: 'Здоровье',
    templateCategoryFitness: 'Фитнес',
    templateCategoryEducation: 'Образование',
    templateCategoryLifestyle: 'Образ жизни',
    templateCategoryBusiness: 'Бизнес',
    templateCategoryBasic: 'Базовый',
    templateCategoryAiCompanion: 'AI-компаньон',
    templateCategoryIqTest: 'IQ-тест',
    templateCategoryAstrology: 'Астрология',
    templateCategoryIncome: 'Доход',
    templateCategoryLanguages: 'Изучение языков',
    templateCategoryStories: 'Истории',
    templateCategoryBeauty: 'Красота',
    templateCategoryMeditation: 'Медитация',
    templateCategoryMentalHealth: 'Ментальное здоровье',
    templateCategoryRelationships: 'Отношения',
    templateCategoryNutrition: 'Питание',
    templateCategoryWeightLoss: 'Похудение',
    templateCategoryProductivity: 'Продуктивность',
    templateCategoryReligion: 'Религия',
    templateCategoryParenting: 'Родительство',
    templateCategorySelfGrowth: 'Саморазвитие',
    templateCategoryStyle: 'Стиль',
    templateCategoryUtilities: 'Утилиты',
    templateCategoryFinance: 'Финансы',
    templateCategoryHobbies: 'Хобби',
    templateCategoryEcommerce: 'Электронная коммерция',
    templateAllLanguages: 'Все языки',
    templateAllCategories: 'Все категории',
    templatePickQuiz: 'Выберите шаблон квиза',
    templatePickPaywall: 'Выберите шаблон пейволла',
    templatesEmpty: 'Нет доступных шаблонов',
    templateSearchPlaceholder: 'Поиск по названию или описанию…',

    noProjectToCreateIn:
      'Нет проекта, в котором вы можете создавать. Попросите доступ к проекту или создайте новый.',
    switchProjectFailed: 'Не удалось переключить проект',
    projectsStillLoading:
      'Загружаем список проектов — попробуйте ещё раз через секунду.',
    projectNoLongerActive:
      'Проект перестал быть активным — выберите его заново и повторите создание.',
    creatingInProject: (projectName: string) =>
      `Создаём в проекте «${projectName}» — кабинет переключён в него.`,

    nfCreatedGo: 'Создано — открываем конструктор',
    nfFixNotice:
      'Часть воронки удалена. Выберите, чем её заменить, — воронка вернётся черновиком.',
    nfFix: 'Починить',
    nfDraftNote: 'Черновик в воронку не взять — сначала опубликуйте его.',
    nfPartKept: 'Остаётся как есть',

    wizardTitle: 'Новая воронка',
    wizardDuplicateName:
      'Воронка с таким именем уже существует в проекте. Измените название и попробуйте снова.',
    wizardPaywallNoneWithQuiz: 'Без пейволла (только квиз)',
    wizardDraftSuffix: (name: string) => `${name} (черновик)`,
    wizardNameRequired: 'Название воронки обязательно',
    wizardQuizSuffix: (name: string) => `${name} — квиз`,
    wizardPaywallSuffix: (name: string) => `${name} — пейволл`,
    wizardCreateQuizFailed: 'Не удалось создать квиз',
    wizardCreateFunnelFailed: 'Не удалось создать воронку',
    wizardCreatePaywallFailed: 'Не удалось создать пейволл',
    wizardCompositionErrorInline: 'Воронке нужен хотя бы квиз или пейволл.',
    wizardFunnelNameLabel: 'Название воронки',
    wizardQuizLabel: 'Квиз',
    wizardPaywallLabel: 'Пейволл',
    wizardCreateNewQuiz: 'Создать новый квиз',
    wizardPickFromLibrary: 'Выбрать из библиотеки',
    wizardNoQuiz: 'Без квиза (только пейволл)',
    wizardSelectQuizPlaceholder: 'Выберите квиз из списка',
    wizardCreateNewPaywall: 'Создать новый пейволл',
    wizardSelectPaywallPlaceholder: 'Выберите пейволл из списка',
    wizardEmptyPaywallWarning: 'Пейволл будет создан пустым',
    orgProjectDomainsNotLoaded: 'Не загрузилось',
    orgProjectDomainsNotLoadedHint:
      'Не удалось загрузить домены организации. Обновите страницу, чтобы попробовать снова.',
  },
);
